// Asset loading with a hard guarantee: a missing or broken sprite never breaks
// the game, it falls back to a procedural primitive and logs (AGENT.md §3).
//
// Misses are cached, so a sprite that does not exist yet is probed once rather
// than on every frame.

import { Assets, MIPMAP_MODES, SCALE_MODES, Texture } from 'pixi.js';
import { bus } from '../core/events';

/**
 * Sprites are authored well above their on-screen size so they survive a 2.5x
 * zoom on a retina display. Without a mip chain that headroom becomes a
 * liability: minifying a 512px tile to 128px with bilinear sampling alone
 * crawls and sparkles on every pan. Pixi only builds mips for power-of-two
 * textures by default, and clay renders are never power-of-two, so ask for
 * them explicitly — WebGL2 has no POT restriction.
 */
const TEXTURE_OPTIONS = {
  mipmap: MIPMAP_MODES.ON,
  scaleMode: SCALE_MODES.LINEAR,
  anisotropicLevel: 4,
};

export type LoadState = 'unloaded' | 'loading' | 'loaded' | 'missing';

export class AssetRegistry {
  private textures = new Map<string, Texture>();
  private states = new Map<string, LoadState>();
  private inFlight = new Map<string, Promise<Texture | null>>();

  /** Synchronous lookup for the render loop. Null means "use the primitive". */
  get(path: string): Texture | null {
    return this.textures.get(path) ?? null;
  }

  stateOf(path: string): LoadState {
    return this.states.get(path) ?? 'unloaded';
  }

  isMissing(path: string): boolean {
    return this.states.get(path) === 'missing';
  }

  /** Loads once; repeated calls share the same promise and never re-probe. */
  async load(path: string): Promise<Texture | null> {
    const cached = this.textures.get(path);
    if (cached) return cached;
    if (this.states.get(path) === 'missing') return null;

    const existing = this.inFlight.get(path);
    if (existing) return existing;

    this.states.set(path, 'loading');
    const promise = Assets.load<Texture>({ src: path, data: TEXTURE_OPTIONS })
      .then((texture) => {
        // Pixi can resolve a placeholder for a 404 depending on server config.
        if (!texture || texture === Texture.EMPTY) throw new Error('empty texture');
        this.textures.set(path, texture);
        this.states.set(path, 'loaded');
        return texture;
      })
      .catch((err) => {
        this.states.set(path, 'missing');
        bus.emit('ASSET_FALLBACK', {
          assetPath: path,
          reason: err instanceof Error ? err.message : String(err),
        });
        return null;
      })
      .finally(() => {
        this.inFlight.delete(path);
      });

    this.inFlight.set(path, promise);
    return promise;
  }

  /**
   * Warms a batch. Resolves when all have settled — never rejects, because a
   * missing sprite is an expected state while art is still being generated.
   */
  async preload(paths: string[]): Promise<{ loaded: number; missing: number }> {
    const results = await Promise.all(paths.map((path) => this.load(path)));
    const loaded = results.filter(Boolean).length;
    return { loaded, missing: results.length - loaded };
  }

  /**
   * Fire-and-forget request used from the render loop: returns the texture if
   * it is already there, and starts loading it if it has never been tried.
   */
  getOrRequest(path: string): Texture | null {
    const texture = this.textures.get(path);
    if (texture) return texture;
    if (this.stateOf(path) === 'unloaded') void this.load(path);
    return null;
  }

  /** Coverage report for the dev dashboard's asset status view. */
  report(): { path: string; state: LoadState }[] {
    return [...this.states.entries()].map(([path, state]) => ({ path, state }));
  }

  clear(): void {
    this.textures.clear();
    this.states.clear();
    this.inFlight.clear();
  }
}

export const assets = new AssetRegistry();
